import { useState, useEffect } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { ChevronDown, ChevronRight, Plus, Trash2 } from 'lucide-react'
import {
  Button,
  ErrorState,
  IconButton,
  Input,
  LoadingState,
  Modal,
  useToast,
} from '@/components/ui'
import {
  convertDocument,
  getDocumentDetail,
  DOC_TYPE_LABELS,
  type CachedDocument,
  type DocType,
  type SapDocLine,
} from '@/api/sapDocuments.ts'

interface EditableLine {
  itemCode: string
  description: string
  quantity: number
  unitPrice: number
  discountPct: number
  uom: string
  vatGroup: string
  warehouseCode: string
  projectCode: string
  /** null = normale Position; Zeilennummer (0-basiert) = Unterartikel dieser Zeile */
  parentLineNum: number | null
  treeType: 'iNotATree' | 'iSalesTree' | 'iSubTree'
}

function docLinesToEditable(lines: SapDocLine[]): EditableLine[] {
  return lines
    .filter((l) => l.LineType !== 'T')
    .map((l) => ({
      itemCode: l.ItemCode ?? '',
      description: l.Dscription ?? l.ItemDescription ?? l.FreeText ?? '',
      quantity: l.Quantity ?? 1,
      unitPrice: l.UnitPrice ?? l.Price ?? 0,
      discountPct: l.DiscountPercent ?? 0,
      uom: l.MeasureUnit ?? l.UnitMsr ?? '',
      vatGroup: l.VatGroup ?? '',
      warehouseCode: l.WarehouseCode ?? '',
      projectCode: l.ProjectCode ?? '',
      parentLineNum: l.ParentLineNum ?? null,
      treeType: l.TreeType ?? 'iNotATree',
    }))
}

interface Props {
  doc: CachedDocument | null
  targetType: DocType | null
  open: boolean
  onOpenChange: (open: boolean) => void
  onSuccess: () => void
}

export function DocumentConvertModal({ doc, targetType, open, onOpenChange, onSuccess }: Props) {
  const { toast } = useToast()
  const queryClient = useQueryClient()

  const [lines, setLines] = useState<EditableLine[]>([])
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set())
  const [isLoadingLines, setIsLoadingLines] = useState(false)
  const [linesError, setLinesError] = useState<string | null>(null)

  useEffect(() => {
    if (!open || !doc) return
    setExpandedRows(new Set())

    if (doc.linesJson) {
      setLines(docLinesToEditable(doc.linesJson))
      setLinesError(null)
      return
    }

    setIsLoadingLines(true)
    setLinesError(null)
    setLines([])
    getDocumentDetail(doc.docType, doc.sapDocEntry, { tenantId: doc.tenantId })
      .then((detail) => {
        setLines(detail.linesJson ? docLinesToEditable(detail.linesJson) : [])
      })
      .catch((err) => {
        setLinesError(err instanceof Error ? err.message : 'Positionen konnten nicht geladen werden')
      })
      .finally(() => setIsLoadingLines(false))
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, doc?.sapDocEntry, doc?.tenantId])

  const { mutate: doConvert, isPending } = useMutation({
    mutationFn: () =>
      convertDocument(doc!.docType, doc!.sapDocEntry, {
        targetType: targetType!,
        modifiedLines: lines.map((l, i) => {
          const base: Record<string, unknown> = {
            LineNum: i,
            ItemCode: l.itemCode || undefined,
            Quantity: l.quantity,
            UnitPrice: l.unitPrice,
            DiscountPercent: l.discountPct,
            FreeText: l.description,
          }
          if (l.uom) base['MeasureUnit'] = l.uom
          if (l.vatGroup) base['VatGroup'] = l.vatGroup
          if (l.warehouseCode) base['WarehouseCode'] = l.warehouseCode
          if (l.projectCode) base['ProjectCode'] = l.projectCode
          if (l.parentLineNum !== null) {
            base['ParentLineNum'] = l.parentLineNum
            base['TreeType'] = 'iSubTree'
          } else if (l.treeType !== 'iNotATree') {
            base['TreeType'] = l.treeType
          }
          return base
        }),
      }),
    onSuccess: () => {
      toast({
        title: 'Umwandlung erfolgreich',
        description: `${DOC_TYPE_LABELS[targetType!]} wurde erstellt.`,
        tone: 'success',
      })
      void queryClient.invalidateQueries({ queryKey: ['sap-documents'] })
      onSuccess()
      onOpenChange(false)
    },
    onError: (err) => {
      toast({
        title: 'Fehler bei der Umwandlung',
        description: err instanceof Error ? err.message : 'Unbekannter Fehler',
        tone: 'danger',
      })
    },
  })

  function updateLine(idx: number, patch: Partial<EditableLine>) {
    setLines((prev) => prev.map((l, i) => (i === idx ? { ...l, ...patch } : l)))
  }

  function removeLine(idx: number) {
    setLines((prev) => prev.filter((_, i) => i !== idx))
    setExpandedRows((prev) => {
      const next = new Set<number>()
      prev.forEach((r) => { if (r < idx) next.add(r); else if (r > idx) next.add(r - 1) })
      return next
    })
  }

  function addLine() {
    setLines((prev) => [
      ...prev,
      { itemCode: '', description: '', quantity: 1, unitPrice: 0, discountPct: 0, uom: '', vatGroup: '', warehouseCode: '', projectCode: '', parentLineNum: null, treeType: 'iNotATree' },
    ])
  }

  function toggleExpand(idx: number) {
    setExpandedRows((prev) => {
      const next = new Set(prev)
      if (next.has(idx)) next.delete(idx); else next.add(idx)
      return next
    })
  }

  if (!doc || !targetType) return null

  const total = lines.reduce((sum, l) => sum + l.quantity * l.unitPrice * (1 - l.discountPct / 100), 0)

  // Options for parent-selection: all lines that are not sub-items (or the current line itself)
  function parentOptions(currentIdx: number) {
    return lines
      .map((l, i) => ({ i, label: l.itemCode ? `${i + 1}. ${l.itemCode}` : `${i + 1}. ${l.description || '—'}` }))
      .filter(({ i }) => i !== currentIdx)
  }

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      size="xl"
      title={`${DOC_TYPE_LABELS[doc.docType]} → ${DOC_TYPE_LABELS[targetType]} umwandeln`}
      description={`Beleg für ${doc.cardName ?? doc.cardCode ?? '—'} — Positionen können vor der Umwandlung angepasst werden.`}
      footer={
        <div className="flex items-center justify-end gap-2">
          <Button variant="secondary" onClick={() => onOpenChange(false)} disabled={isPending}>
            Abbrechen
          </Button>
          <Button variant="primary" onClick={() => doConvert()} disabled={isPending || isLoadingLines || !!linesError || lines.length === 0}>
            {isPending ? 'Wird erstellt…' : `${DOC_TYPE_LABELS[targetType]} erstellen`}
          </Button>
        </div>
      }
    >
      <div className="space-y-1 max-h-[60vh] overflow-y-auto">
        {isLoadingLines && <LoadingState label="Positionen werden geladen…" />}

        {linesError && (
          <ErrorState
            title="Positionen konnten nicht geladen werden"
            description={linesError}
          />
        )}

        {!isLoadingLines && !linesError && (
          <>
            {/* Kopfzeile */}
            <div className="grid grid-cols-[20px_1fr_2fr_72px_96px_64px_32px] gap-1.5 text-caption font-medium text-gray-500 px-1 mb-1">
              <span />
              <span>Artikelnr.</span>
              <span>Beschreibung</span>
              <span>Menge</span>
              <span>Preis</span>
              <span>Rabatt</span>
              <span />
            </div>

            {lines.map((line, idx) => {
              const isExpanded = expandedRows.has(idx)
              const parents = parentOptions(idx)
              return (
                <div key={idx} className="rounded-md border border-gray-100 bg-white">
                  {/* Hauptzeile */}
                  <div className="grid grid-cols-[20px_1fr_2fr_72px_96px_64px_32px] gap-1.5 items-center px-1 py-1">
                    <button
                      type="button"
                      className="flex items-center justify-center text-gray-400 hover:text-gray-600"
                      onClick={() => toggleExpand(idx)}
                      aria-label={isExpanded ? 'Zeilendetails ausblenden' : 'Zeilendetails anzeigen'}
                    >
                      {isExpanded
                        ? <ChevronDown className="w-3.5 h-3.5" />
                        : <ChevronRight className="w-3.5 h-3.5" />}
                    </button>
                    <Input
                      value={line.itemCode}
                      onChange={(e) => updateLine(idx, { itemCode: e.target.value })}
                      placeholder="Artikelnr."
                      className="py-1 text-sm"
                    />
                    <Input
                      value={line.description}
                      onChange={(e) => updateLine(idx, { description: e.target.value })}
                      placeholder="Bezeichnung"
                      className="py-1 text-sm"
                    />
                    <Input
                      type="number"
                      value={String(line.quantity)}
                      onChange={(e) => updateLine(idx, { quantity: Number(e.target.value) })}
                      min={0}
                      className="py-1 text-sm"
                    />
                    <Input
                      type="number"
                      value={String(line.unitPrice)}
                      onChange={(e) => updateLine(idx, { unitPrice: Number(e.target.value) })}
                      min={0}
                      step={0.01}
                      className="py-1 text-sm"
                    />
                    <Input
                      type="number"
                      value={String(line.discountPct)}
                      onChange={(e) => updateLine(idx, { discountPct: Number(e.target.value) })}
                      min={0}
                      max={100}
                      className="py-1 text-sm"
                    />
                    <IconButton
                      icon={<Trash2 className="w-3.5 h-3.5" />}
                      aria-label="Position entfernen"
                      variant="ghost"
                      onClick={() => removeLine(idx)}
                    />
                  </div>

                  {/* Erweiterungsbereich */}
                  {isExpanded && (
                    <div className="border-t border-gray-100 bg-gray-50 px-3 py-2 space-y-2">
                      <div className="grid grid-cols-2 gap-x-4 gap-y-2 sm:grid-cols-4">
                        <div>
                          <label className="text-caption font-medium text-gray-500 block mb-0.5">Einheit</label>
                          <Input
                            value={line.uom}
                            onChange={(e) => updateLine(idx, { uom: e.target.value })}
                            placeholder="z. B. STK"
                            className="py-1 text-sm"
                          />
                        </div>
                        <div>
                          <label className="text-caption font-medium text-gray-500 block mb-0.5">MwSt-Code</label>
                          <Input
                            value={line.vatGroup}
                            onChange={(e) => updateLine(idx, { vatGroup: e.target.value })}
                            placeholder="z. B. A1"
                            className="py-1 text-sm"
                          />
                        </div>
                        <div>
                          <label className="text-caption font-medium text-gray-500 block mb-0.5">Lager</label>
                          <Input
                            value={line.warehouseCode}
                            onChange={(e) => updateLine(idx, { warehouseCode: e.target.value })}
                            placeholder="z. B. 01"
                            className="py-1 text-sm"
                          />
                        </div>
                        <div>
                          <label className="text-caption font-medium text-gray-500 block mb-0.5">Projekt</label>
                          <Input
                            value={line.projectCode}
                            onChange={(e) => updateLine(idx, { projectCode: e.target.value })}
                            placeholder="Projektcode"
                            className="py-1 text-sm"
                          />
                        </div>
                      </div>

                      {/* Parent item (BOM) */}
                      <div>
                        <label className="text-caption font-medium text-gray-500 block mb-0.5">
                          Übergeordnet (Stücklisten-Unterartikel)
                        </label>
                        <select
                          value={line.parentLineNum ?? ''}
                          onChange={(e) => {
                            const val = e.target.value
                            updateLine(idx, {
                              parentLineNum: val === '' ? null : Number(val),
                              treeType: val === '' ? 'iNotATree' : 'iSubTree',
                            })
                          }}
                          className="w-full rounded-md border border-gray-300 bg-white px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
                        >
                          <option value="">— keine (eigenständige Position) —</option>
                          {parents.map(({ i, label }) => (
                            <option key={i} value={i}>
                              {label}
                            </option>
                          ))}
                        </select>
                        {line.parentLineNum !== null && (
                          <p className="mt-0.5 text-caption text-gray-400">
                            Diese Position wird als Unterartikel von Zeile {line.parentLineNum + 1} behandelt.
                          </p>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )
            })}

            <Button variant="ghost" size="sm" onClick={addLine} className="gap-1.5 mt-1">
              <Plus className="w-3.5 h-3.5" />
              Position hinzufügen
            </Button>

            <div className="border-t border-gray-200 pt-3 flex justify-end">
              <span className="text-body font-semibold text-gray-900">
                Gesamt: {total.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €
              </span>
            </div>
          </>
        )}
      </div>
    </Modal>
  )
}
